import { FC, useEffect, useRef, useState } from 'react';
import ReusableModal from '../common/Modal';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Skeleton,
  Menu,
  MenuItem,
  Tooltip,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';
import {
  CheckIcon,
  CopyIcon,
  DotsThreeVerticalIcon,
  PencilSimpleIcon,
  ArrowLeftIcon,
} from '@phosphor-icons/react';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getVoiceUrlApi, updatePlivoNumberUrlApi } from '../../utils/apis';
import { useDashboard } from '../../hooks/useDashboard';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';

interface DeployUrlsModalProps {
  open: boolean;
  onClose: () => void;
}

interface EditUrlData {
  provider: string;
  baseUrl: string;
  urlPath: string;
  phoneNumber: string;
}

interface PhoneNumberData {
  provider: string;
  phoneNumber: string;
}

const DeployUrlsModal: FC<DeployUrlsModalProps> = ({ open, onClose }) => {
  const { selectedAssistant } = useDashboard();
  const { copy } = useCopyToClipboard();
  const { showAlert, alert, setAlert } = useAlert();
  const queryClient = useQueryClient();
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState<EditUrlData>({
    provider: '',
    baseUrl: '',
    urlPath: '',
    phoneNumber: '',
  });

  // Enhanced URL splitting function specifically for provider webhooks
  const intelligentSplitUrl = (fullUrl: string) => {
    if (!fullUrl.trim())
      return { baseUrl: '', urlPath: '', suggestedSplits: [] };

    try {
      const url = new URL(fullUrl);
      const fullPath = `${url.pathname}${url.search}${url.hash}`;
      const baseHost = `${url.protocol}//${url.host}`;

      // Detect potential split points
      const suggestedSplits = [];

      // Always suggest domain-only split
      suggestedSplits.push({
        label: 'Domain only',
        baseUrl: baseHost,
        urlPath: fullPath,
      });

      // Look for provider-specific patterns in order of preference
      const providerPatterns = [
        { pattern: '/plivo/', label: 'Before Plivo path' },
        { pattern: '/twilio/', label: 'Before Twilio path' },
        { pattern: '/vonage/', label: 'Before Vonage path' },
        { pattern: '/telnyx/', label: 'Before Telnyx path' },
        { pattern: '/signalwire/', label: 'Before SignalWire path' },
        { pattern: '/incoming-call', label: 'Before incoming-call' },
        { pattern: '/webhook', label: 'Before webhook' },
        { pattern: '/voice', label: 'Before voice path' },
        { pattern: '/call', label: 'Before call path' },
      ];

      let defaultSplit = suggestedSplits[0];

      for (const { pattern, label } of providerPatterns) {
        const index = fullPath.toLowerCase().indexOf(pattern.toLowerCase());
        if (index !== -1) {
          const beforePattern = fullPath.substring(0, index);
          const suggestion = {
            label,
            baseUrl: baseHost + beforePattern,
            urlPath: fullPath.substring(index),
          };
          suggestedSplits.push(suggestion);

          // Set the first provider pattern found as default
          if (defaultSplit === suggestedSplits[0]) {
            defaultSplit = suggestion;
          }
        }
      }

      return {
        baseUrl: defaultSplit.baseUrl,
        urlPath: defaultSplit.urlPath,
        suggestedSplits,
      };
    } catch {
      return {
        baseUrl: fullUrl,
        urlPath: '',
        suggestedSplits: [{ label: 'Full URL', baseUrl: fullUrl, urlPath: '' }],
      };
    }
  };

  // Function to apply a suggested split
  // const applySuggestedSplit = (suggestion: {
  //   baseUrl: string;
  //   urlPath: string;
  // }) => {
  //   setEditData({
  //     ...editData,
  //     baseUrl: suggestion.baseUrl,
  //     urlPath: suggestion.urlPath,
  //   });
  // };

  // Utility function to combine base URL and path
  const combineUrl = (baseUrl: string, urlPath: string) => {
    if (!baseUrl.trim()) return '';
    const cleanBaseUrl = baseUrl.trim().replace(/\/$/, '');
    const cleanPath = urlPath.trim();
    return cleanPath ? `${cleanBaseUrl}${cleanPath}` : cleanBaseUrl;
  };

  // Filter out empty phone numbers and create data structure
  const phoneNumbersData: PhoneNumberData[] = selectedAssistant?.assignedNumbers
    ? Object.entries(selectedAssistant.assignedNumbers).flatMap(
        ([provider, phoneNumbers]) => {
          // Handle both array and string formats for backward compatibility
          const numbers = Array.isArray(phoneNumbers)
            ? phoneNumbers
            : [phoneNumbers];

          return numbers
            .filter(
              phoneNumber =>
                typeof phoneNumber === 'string' && phoneNumber.trim() !== ''
            )
            .map(phoneNumber => ({
              provider,
              phoneNumber: phoneNumber,
            }));
        }
      )
    : [];

  // Use a single query to fetch all voice URLs
  const voiceUrlsQuery = useQuery({
    queryKey: ['voiceUrls', phoneNumbersData],
    queryFn: async () => {
      const promises = phoneNumbersData.map(data =>
        getVoiceUrlApi({
          phoneNumber: data.phoneNumber,
          provider: data.provider,
        })
      );
      // Important: one provider/number failing (e.g., 404) should not block
      // displaying URLs for the other numbers.
      const settled = await Promise.allSettled(promises);
      return settled.map(result =>
        result.status === 'fulfilled' ? result.value : null
      );
    },
    enabled: open && phoneNumbersData.length > 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 0,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  const hasShownPartialUrlFailureAlertRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasShownPartialUrlFailureAlertRef.current = false;
      return;
    }

    if (
      voiceUrlsQuery.status === 'success' &&
      voiceUrlsQuery.data?.some(r => r === null) &&
      !hasShownPartialUrlFailureAlertRef.current
    ) {
      hasShownPartialUrlFailureAlertRef.current = true;
      showAlert('Some phone numbers do not have a current URL', 'error');
    }
  }, [open, showAlert, voiceUrlsQuery.data, voiceUrlsQuery.status]);

  // Add mutation for updating the URL
  const updateUrlMutation = useMutation({
    mutationFn: updatePlivoNumberUrlApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voiceUrls'] });
      showAlert('URL updated successfully', 'success');
      handleBackToTable();
    },
    onError: () => {
      showAlert('Failed to update URL', 'error');
    },
  });

  const handleCopyUrl = (url: string, index: number) => {
    copy(url);
    setCopiedIndex(index);
    setTimeout(() => {
      setCopiedIndex(null);
    }, 3000);
    handleMenuClose();
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowIndex(null);
  };

  // Update the handleEditUrl function to use the intelligent splitting
  const handleEditUrl = (index: number) => {
    const data = phoneNumbersData[index];
    const voiceUrlData = voiceUrlsQuery.data?.[index]?.data;
    const fullUrl = voiceUrlData?.currentUrl || '';

    const { baseUrl, urlPath } = intelligentSplitUrl(fullUrl);

    setEditData({
      provider: data.provider,
      phoneNumber: data.phoneNumber,
      baseUrl,
      urlPath,
    });
    setIsEditMode(true);
    handleMenuClose();
  };

  const handleBackToTable = () => {
    setIsEditMode(false);
    setEditData({ provider: '', baseUrl: '', urlPath: '', phoneNumber: '' });
  };

  const handleSaveEdit = () => {
    const fullUrl = combineUrl(editData.baseUrl, editData.urlPath);
    if (!fullUrl.trim()) return;

    updateUrlMutation.mutate({
      phoneNumber: editData.phoneNumber,
      newUrl: fullUrl,
      provider: editData.provider,
    });
  };

  // Placeholder skeleton for loading
  const renderSkeletonRows = () =>
    Array.from({ length: 3 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell>
          <Skeleton variant="text" width={100} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width={120} />
        </TableCell>
        <TableCell>
          <Skeleton variant="text" width="80%" />
        </TableCell>
        <TableCell align="center">
          <Skeleton variant="circular" width={24} height={24} />
        </TableCell>
      </TableRow>
    ));

  const renderDataRows = () => {
    if (!phoneNumbersData || phoneNumbersData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
            No assigned phone numbers available
          </TableCell>
        </TableRow>
      );
    }

    return phoneNumbersData.map((data, index) => {
      const isLoading = voiceUrlsQuery.isLoading;
      const voiceUrlData = voiceUrlsQuery.data?.[index]?.data;
      const url = voiceUrlData?.currentUrl || ''; // Changed from 'url' to 'currentUrl'

      return (
        <TableRow key={index}>
          <TableCell sx={{ fontWeight: 600, color: 'custom.textBlue' }}>
            {data.provider.charAt(0).toUpperCase() + data.provider.slice(1)}
          </TableCell>
          <TableCell sx={{ color: 'text.secondary' }}>
            {data.phoneNumber}
          </TableCell>
          <TableCell sx={{ color: 'text.secondary' }}>
            {isLoading ? (
              <Skeleton variant="text" width="80%" />
            ) : url ? (
              url
            ) : (
              'No URL available'
            )}
          </TableCell>
          <TableCell align="center">
            <Tooltip title="Actions">
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={e => handleMenuOpen(e, index)}
              >
                <DotsThreeVerticalIcon size={14} />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>
      );
    });
  };

  const renderEditView = () => {
    const fullUrl = combineUrl(editData.baseUrl, editData.urlPath);

    // Get the original URL to show split options
    // const originalUrl =
    //   voiceUrlsQuery.data?.find(
    //     (_, index) =>
    //       phoneNumbersData[index]?.phoneNumber === editData.phoneNumber
    //   )?.data?.currentUrl || '';

    // const { suggestedSplits } = intelligentSplitUrl(originalUrl);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'text.primary',
              fontSize: '14px',
            }}
          >
            Provider
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={
              editData.provider.charAt(0).toUpperCase() +
              editData.provider.slice(1)
            }
            disabled
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                backgroundColor: '#F8F9FC',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#666',
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'text.primary',
              fontSize: '14px',
            }}
          >
            Phone Number
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={editData.phoneNumber}
            disabled
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                backgroundColor: '#F8F9FC',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#666',
              },
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'text.primary',
              fontSize: '14px',
            }}
          >
            Base URL (Editable)
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Enter base URL (e.g., https://your-domain.ngrok-free.app)"
            value={editData.baseUrl}
            onChange={e =>
              setEditData({ ...editData, baseUrl: e.target.value })
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
              },
            }}
          />
        </Box>

        {editData.urlPath && (
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.primary',
                fontSize: '14px',
              }}
            >
              Provider Path (Read-only)
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={editData.urlPath}
              disabled
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '6px',
                  backgroundColor: '#F8F9FC',
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#666',
                },
              }}
            />
          </Box>
        )}

        {/* Full URL Preview */}
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'text.primary',
              fontSize: '14px',
            }}
          >
            Complete URL Preview
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={fullUrl || 'Enter base URL to see preview'}
            disabled
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                backgroundColor: '#F8F9FC',
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                wordBreak: 'break-all',
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#666',
              },
            }}
          />
        </Box>

        <Box
          sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}
        >
          <Button
            variant="outlined"
            onClick={handleBackToTable}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              borderColor: 'lightGray.main',
              color: 'text.secondary',
              fontSize: '14px',
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={!editData.baseUrl.trim() || updateUrlMutation.isPending}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              backgroundColor: '#5A4CE6',
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#4A3ED6',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E0E0E0',
                color: '#A0A4B8',
              },
            }}
          >
            {updateUrlMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <ReusableModal
        open={open}
        onClose={onClose}
        title={
          isEditMode ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={handleBackToTable}>
                <ArrowLeftIcon size={16} />
              </IconButton>
              <Typography>
                Edit{' '}
                {editData.provider.charAt(0).toUpperCase() +
                  editData.provider.slice(1)}{' '}
                URL
              </Typography>
            </Box>
          ) : (
            'Deploy URLs'
          )
        }
        width={800}
      >
        {isEditMode ? (
          renderEditView()
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'text.primary' }}>
                      Provider
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>
                      Phone Number
                    </TableCell>
                    <TableCell sx={{ color: 'text.primary' }}>URL</TableCell>
                    <TableCell sx={{ color: 'text.primary' }} align="center">
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voiceUrlsQuery.isLoading
                    ? renderSkeletonRows()
                    : renderDataRows()}
                </TableBody>
              </Table>
            </TableContainer>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{
                '& .MuiPaper-root': {
                  borderRadius: '12px',
                  boxShadow: 1,
                  width: '150px',
                  border: '1px solid #E0E0E0',
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  if (selectedRowIndex !== null) {
                    const voiceUrlData =
                      voiceUrlsQuery.data?.[selectedRowIndex]?.data;
                    const url = voiceUrlData?.currentUrl;
                    if (url) {
                      handleCopyUrl(url, selectedRowIndex);
                    }
                  }
                }}
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {copiedIndex === selectedRowIndex ? (
                  <CheckIcon size={18} />
                ) : (
                  <CopyIcon size={18} />
                )}
                Copy URL
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (selectedRowIndex !== null) {
                    handleEditUrl(selectedRowIndex);
                  }
                }}
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <PencilSimpleIcon size={18} />
                Edit URL
              </MenuItem>
            </Menu>
          </>
        )}
      </ReusableModal>
      {alert && <MessagePopup alert={alert} setAlert={setAlert} />}
    </>
  );
};

export default DeployUrlsModal;
